import React, { useState } from 'react';
import { Play, Youtube, Shield, ExternalLink } from 'lucide-react';

/**
 * Composant d'intégration YouTube RGPD-safe + performant.
 *
 * Comportement :
 *   - Par défaut : affiche un PLACEHOLDER (miniature YouTube via i.ytimg.com, légitime)
 *     avec un bouton Play et le titre. AUCUN script YouTube chargé, AUCUN cookie.
 *   - Au clic : charge l'iframe via `youtube-nocookie.com` (mode privacy-enhanced YouTube)
 *     qui ne dépose aucun cookie tant que l'utilisateur n'a pas lancé la lecture.
 *
 * Cette approche est conforme au RGPD et au conseil CNIL 2022 sur les vidéos externes :
 * tant que l'utilisateur n'a pas explicitement cliqué pour lire, aucune donnée n'est
 * transmise à YouTube.
 *
 * Props :
 *   - videoId   : ID YouTube (ex: "dQw4w9WgXcQ")
 *   - listId    : ID de playlist YouTube (ex: "PLKAhYoO...") — OPTIONNEL
 *   - title     : Titre affiché
 *   - channel   : Nom de la chaîne (crédit)
 *   - aspect    : "16/9" (par défaut) ou "9/16" pour shorts
 *   - testid    : data-testid
 */
export function SafeVideoEmbed({
  videoId,
  listId,
  title = 'Vidéo officielle',
  channel = 'Sécurité routière',
  aspect = '16/9',
  testid = 'safe-video-embed',
  className = '',
}) {
  const [loaded, setLoaded] = useState(false);

  // Construit l'URL embed selon qu'on passe un ID de vidéo ou une playlist
  const embedUrl = (() => {
    const base = 'https://www.youtube-nocookie.com/embed';
    if (listId) {
      // Playlist : on peut soit démarrer au début, soit sur une vidéo précise
      if (videoId) {
        return `${base}/${videoId}?list=${listId}&autoplay=1&rel=0&modestbranding=1`;
      }
      return `${base}/videoseries?list=${listId}&autoplay=1&rel=0&modestbranding=1`;
    }
    return `${base}/${videoId}?autoplay=1&rel=0&modestbranding=1`;
  })();

  // Miniature YouTube (via i.ytimg.com — image statique, pas de tracking)
  const thumbnailUrl = videoId
    ? `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`
    : null;

  const aspectClass = aspect === '9/16' ? 'aspect-[9/16]' : 'aspect-video';

  return (
    <div
      className={`relative overflow-hidden rounded-xl border border-border bg-black ${aspectClass} ${className}`}
      data-testid={testid}
    >
      {!loaded ? (
        <button
          type="button"
          onClick={() => setLoaded(true)}
          className="group absolute inset-0 w-full h-full flex items-center justify-center bg-[hsl(var(--panel))] text-foreground"
          aria-label={`Lire la vidéo : ${title}`}
          data-testid={`${testid}-play-btn`}
        >
          {/* Miniature YouTube (si disponible) */}
          {thumbnailUrl && (
            <img
              src={thumbnailUrl}
              alt=""
              aria-hidden="true"
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-80 transition-opacity"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          )}

          {/* Overlay sombre pour lisibilité */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20" />

          {/* Bouton central */}
          <div className="relative z-10 flex flex-col items-center gap-3 px-4">
            <div className="w-16 h-16 rounded-full bg-[hsl(var(--primary))] text-white grid place-items-center shadow-lg group-hover:scale-110 transition-transform">
              <Play className="w-7 h-7 ml-1" fill="currentColor" />
            </div>
            <div className="text-center">
              <p className="font-bold text-sm sm:text-base line-clamp-2 max-w-md">{title}</p>
              <div className="flex items-center justify-center gap-1.5 text-[11px] text-white/80 mt-1">
                <Youtube className="w-3 h-3" />
                <span>{channel}</span>
              </div>
            </div>
            <div className="inline-flex items-center gap-1.5 text-[10px] text-white/70 px-2 py-1 rounded-full bg-white/10 border border-white/20">
              <Shield className="w-3 h-3" />
              <span>Aucun cookie avant lecture</span>
            </div>
          </div>
        </button>
      ) : (
        <iframe
          src={embedUrl}
          title={title}
          className="absolute inset-0 w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
          loading="lazy"
          data-testid={`${testid}-iframe`}
        />
      )}
    </div>
  );
}

/**
 * Bandeau de crédit sous une vidéo (conforme CGU YouTube + respect de la source).
 */
export function VideoCredit({ channel, channelUrl, className = '' }) {
  return (
    <a
      href={channelUrl}
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex items-center gap-1.5 text-[11px] text-muted-foreground hover:text-[hsl(var(--primary))] transition-colors ${className}`}
    >
      <Youtube className="w-3 h-3" />
      <span>Source : {channel}</span>
      <ExternalLink className="w-2.5 h-2.5 opacity-60" />
    </a>
  );
}

export default SafeVideoEmbed;
